/**
 * Welcome screen displayed in the chat area when a conversation has no messages yet.
 * Shows a title, description, and optional quick action buttons.
 */
import { ArrowDownIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { ChatWelcomeQuickAction } from './chat-welcome-quick-action.atom'
import type { ChatWelcomeScreenProps } from './chat-welcome-screen.interface'

const ChatWelcomeScreenBase: React.FC<ChatWelcomeScreenProps> = ({
  title,
  description,
  quickActionList,
  chatHint,
  logoElement,
  className,
}): ReactNode => (
  <div
    className={cn(
      'chat-welcome-screen flex h-full flex-col items-center justify-center px-4 text-center',
      className
    )}
  >
    {logoElement && <div className="chat-welcome-logo mb-5">{logoElement}</div>}
    <h2 className="chat-welcome-title mb-3 text-2xl font-semibold tracking-tight">{title}</h2>
    <p className="chat-welcome-description mb-6 max-w-lg text-sm leading-relaxed text-muted-foreground">
      {description}
    </p>
    {quickActionList && quickActionList.length > 0 && (
      <div className="chat-welcome-actions flex flex-wrap items-center justify-center gap-2">
        {quickActionList.map((action) => (
          <ChatWelcomeQuickAction
            key={action.label}
            icon={action.icon}
            label={action.label}
            onClick={action.onClick}
          />
        ))}
      </div>
    )}
    {chatHint && (
      <div className="chat-welcome-hint mt-6 flex flex-col items-center gap-1 text-sm text-muted-foreground">
        <span>{chatHint}</span>
        <ArrowDownIcon className="size-4 animate-bounce" />
      </div>
    )}
  </div>
)

export const ChatWelcomeScreen = memo(ChatWelcomeScreenBase)

ChatWelcomeScreen.displayName = 'ChatWelcomeScreen'
